import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getReport, updateStatus, listApplications } from "@/api/applications";
import { scoreTone, statusDot, CANONICAL_STATES } from "@/format";
import { cn } from "@/cn";

const TONE_BG: Record<string, string> = {
  good: "bg-emerald-500/10 text-emerald-400",
  warn: "bg-amber-500/10 text-amber-400",
  bad: "bg-red-500/10 text-red-400",
  muted: "bg-zinc-500/10 text-muted-foreground",
};

export function ReportPage() {
  const { id } = useParams<{ id: string }>();
  const num = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch the application to get metadata
  const appsQuery = useQuery({
    queryKey: ["applications", num],
    queryFn: async () => {
      const resp = await listApplications({ limit: 200, offset: 0 });
      const app = resp.applications.find((a) => a.number === num);
      if (!app) throw new Error("Application not found");
      return app;
    },
    enabled: !Number.isNaN(num),
  });

  const app = appsQuery.data;

  // Fetch the report markdown
  const reportQuery = useQuery({
    queryKey: ["report", num],
    queryFn: () => getReport(num),
    enabled: !Number.isNaN(num),
  });

  // Status update mutation
  const statusMutation = useMutation({
    mutationFn: (status: string) => updateStatus(num, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
      queryClient.invalidateQueries({ queryKey: ["pipeline"] });
      queryClient.invalidateQueries({ queryKey: ["report"] });
    },
  });

  const [selectedStatus, setSelectedStatus] = useState<string>("");

  // Update local state when app loads
  if (app && !selectedStatus) {
    setSelectedStatus(app.status);
  }

  const scoreToneResult = app ? scoreTone(String(app.score)) : "muted";

  // Render markdown as simple HTML (no library needed for basic rendering)
  function renderMarkdown(md: string): string {
    let html = md
      // Headers
      .replace(/^### (.+)$/gm, "<h3 class='text-lg font-semibold text-foreground mt-6 mb-2'>$1</h3>")
      .replace(/^## (.+)$/gm, "<h2 class='text-xl font-bold text-foreground mt-8 mb-3'>$1</h2>")
      .replace(/^# (.+)$/gm, "<h1 class='text-2xl font-bold text-foreground mt-8 mb-4'>$1</h1>")
      // Bold / italic
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      // Inline code
      .replace(/`(.+?)`/g, "<code class='rounded bg-muted/30 px-1 py-0.5 text-sm font-mono'>$1</code>")
      // Links
      .replace(/\[(.+?)\]\((.+?)\)/g, "<a href='$2' class='text-brand hover:underline' target='_blank' rel='noopener'>$1</a>")
      // Bullet lists
      .replace(/^- (.+)$/gm, "<li class='ml-4 text-muted-foreground'>$1</li>")
      // Horizontal rule
      .replace(/^---$/gm, "<hr class='my-6 border-border' />")
      // Paragraphs: double newlines
      .replace(/\n\n/g, "</p><p class='text-muted-foreground leading-relaxed'>")
      // Single newlines in paragraphs become breaks
      .replace(/\n/g, "<br />");

    html = `<p class='text-muted-foreground leading-relaxed'>${html}</p>`;
    return html;
  }

  if (Number.isNaN(num)) {
    return (
      <>
        <p className="text-destructive">Invalid report ID.</p>
        <Link to="/sequences" className="text-brand hover:underline">
          &larr; Back to pipeline
        </Link>
      </>
    );
  }

  return (
    <>
      {/* Back link */}
      <Link
        to="/sequences"
        className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        Back to pipeline
      </Link>

      {/* Loading */}
      {(appsQuery.isLoading || reportQuery.isLoading) && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
            <p className="text-sm text-muted-foreground">
              Loading report…
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {appsQuery.error && !appsQuery.isLoading && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">
            Application not found.{" "}
            <Link to="/sequences" className="underline">
              Return to pipeline
            </Link>
          </p>
        </div>
      )}

      {/* Report content */}
      {app && !appsQuery.isLoading && (
        <>
          {/* Header card */}
          <div className="mb-6 rounded-lg border border-border bg-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-xs font-mono text-muted-foreground">
                    #{app.number}
                  </span>
                  <span className="text-xs text-muted-foreground">|</span>
                  <span className="text-xs text-muted-foreground">{app.company}</span>
                </div>
                <h1 className="text-xl font-bold text-foreground">
                  {app.role}
                </h1>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span
                  className={cn(
                    "inline-flex items-center rounded-full px-3 py-1 font-mono text-sm font-semibold",
                    TONE_BG[scoreToneResult],
                  )}
                >
                  {app.score}/5
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={cn(
                      "inline-block h-2.5 w-2.5 rounded-full",
                      statusDot(app.status),
                    )}
                  />
                  <span className="text-sm text-foreground">{app.status}</span>
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
              {app.jobUrl && (
                <a
                  href={app.jobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/20"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                  Open URL
                </a>
              )}
              <button
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/20"
                disabled
                title="PDF generation coming soon"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Generate PDF
              </button>

              {/* Status dropdown */}
              <div className="ml-auto flex items-center gap-2">
                <label
                  htmlFor="report-status"
                  className="text-xs text-muted-foreground"
                >
                  Status:
                </label>
                <select
                  id="report-status"
                  value={selectedStatus}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    setSelectedStatus(newStatus);
                    statusMutation.mutate(newStatus);
                  }}
                  disabled={statusMutation.isPending}
                  className="rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-50"
                >
                  {CANONICAL_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {statusMutation.isPending && (
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted border-t-brand" />
                )}
                {statusMutation.isSuccess && (
                  <svg className="h-3.5 w-3.5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                )}
              </div>
            </div>
          </div>

          {/* Report body */}
          <div className="rounded-lg border border-border bg-card p-6">
            {reportQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-brand" />
                Loading report content…
              </div>
            ) : reportQuery.error ? (
              <div className="rounded-md border border-warning/30 bg-warning/10 px-4 py-3">
                <p className="text-sm text-warning">
                  Could not load report content. The report file may not exist
                  yet.
                </p>
              </div>
            ) : reportQuery.data?.content || reportQuery.data?.report ? (
              <div
                className="prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{
                  __html: renderMarkdown(reportQuery.data.content ?? reportQuery.data.report),
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                No report content available.
              </p>
            )}
          </div>
        </>
      )}
    </>
  );
}